import { computed, type Ref } from 'vue';
import { type FieldFunctionOptions } from '@apollo/client/cache';
import { gql, type NetworkStatus } from '@apollo/client/core';
import uniqBy from 'lodash/uniqBy';
import {
	type LiterallyAnything,
	type NotWorthIt,
	type ValueOf,
} from '@/types/utils';
import {
	type SocketListeners,
	type BaseSocketMessage,
	type SocketMessage,
	useSocket,
} from '@/composables/socket';
import { type concatPagination } from '@apollo/client/utilities';
import { type TYPES } from '@/generated/graphql';
import debounce from 'lodash/debounce';

/**
 * These parameters allow Apollo Client to first return data from the cache and
 * then silently make a request to re-fetch the data from the API.
 *
 * When using these settings, the returned `loading` flag will return `true`
 * even if the result is already in the cache. Use the
 * `usePersistedQueryLoading` composable for correctly calculating whether or
 * not a loading state should be shown.
 */
export const APOLLO_PARAMS_PERSISTED_QUERY = {
	fetchPolicy: 'cache-and-network',
	notifyOnNetworkStatusChange: true,
} as const;

/**
 * Calculate the loading state for persisted queries.
 *
 * Persisted queries will return `true` for the loading state even if the
 * response is already set in the cache and there is a re-fetch query in
 * progress. We usually don't want to show the loading state in that case and
 * need a bit more sophisticated loading state calculation.
 *
 * @example
 * ```js
 * const { result, loading, networkStatus } = usePersistedQueryLoading(
 *   useBookmarksShortcutLazyQuery({
 *     ...APOLLO_PARAMS_PERSISTED_QUERY,
 *   })
 * );
 * // loading is now correctly set
 * ````
 */
export const usePersistedQueryLoading = <
	T extends {
		loading: Ref<boolean>;
		networkStatus: Ref<NetworkStatus | undefined>;
	},
>({
	loading,
	networkStatus,
	...rest
}: T) => {
	const isLoading = computed(() => {
		// Use `any` as a workaround so that the TypeScript typing would be
		// easier, we don't know the exact shape of the result and it would be
		// annoying to type the return value if we accept `result` explicitly
		return loading.value && !(rest as NotWorthIt)?.result?.value;
	});

	return {
		...rest,
		loading: isLoading,
		/**
		 * Original loading not affected by the network status. Should always
		 * be `true` whenever a request is in progress.
		 */
		originalLoading: loading,
		networkStatus,
	};
};

export const DEFAULT_REFETCH_DEBOUNCE_TIME = 500;

/**
 * Refetch the results whenever the given socket messages are received. Also,
 * accepts a filter function to further filter the messages based on the message
 * contents.
 *
 * **Note:** In order to silently re-fetch (without showing a loading
 * indicator), use the `usePersistedQueryLoading` composable and
 * `APOLLO_PARAMS_PERSISTED_QUERY` options.
 */
export const useRefetchOnSocketMessage = <
	T extends { refetch: () => void },
	Message extends keyof SocketMessage,
>(
	{ refetch, ...rest }: T,
	{
		messages,
		filter,
		debounceTime = () => DEFAULT_REFETCH_DEBOUNCE_TIME,
	}: {
		messages: Message[];
		filter: (
			message: BaseSocketMessage<Message, SocketMessage[Message]>
		) => boolean;
		debounceTime?: () => number;
	}
) => {
	const handleSocketMessage = async (
		message: BaseSocketMessage<Message, SocketMessage[Message]>
	) => {
		if (filter(message)) {
			return refetch();
		}
	};

	const handleSocketMessageDebounced = debounce(
		handleSocketMessage,
		debounceTime(),
		{
			maxWait: debounceTime() * 2,
		}
	);

	const getListeners = () => {
		return messages.reduce((acc, curr) => {
			// The message argument type for handleSocketMessage does not really match
			acc[curr] = handleSocketMessageDebounced as NotWorthIt;
			return acc;
		}, {} as Partial<SocketListeners>);
	};

	const socket = useSocket(getListeners());

	const reAttachSocketListeners = () => {
		socket.removeListeners();
		socket.attachListeners(getListeners());
	};

	return {
		refetch,
		reAttachSocketListeners,
		...rest,
	};
};

/**
 * Copy of the `concatPagination` from Apollo Client
 * (https://github.com/apollographql/apollo-client/blob/18e9911116902870d0cbb8a10d3174e24f5817d9/src/utilities/policies/pagination.ts#L10)
 * which ignores duplicates in the results.
 */
export const concatPaginationUnique: typeof concatPagination = (
	keyArgs = false
) => ({
	keyArgs,
	merge(existing, incoming) {
		return uniqBy(
			existing ? [...existing, ...incoming] : incoming,
			'__ref'
		);
	},
});

/**
 * Check if we have an existing quote already saved
 * into Apollo Cache. This is mostly required in
 * tests, since for some reason, returning a `ref`
 * to a non-existing object returns `{}` as the
 * query response (but we want it to just wait for
 * the API response).
 */
export const detailsPrefilledWithCachedData =
	(type: ValueOf<typeof TYPES>, fields: string) =>
	(
		existing: LiterallyAnything,
		{
			args,
			cache,
			toReference,
		}: FieldFunctionOptions<
			Record<string, LiterallyAnything>,
			Record<string, LiterallyAnything>
		>
	) => {
		if (!args || existing) {
			return existing;
		}

		const ref = toReference({
			__typename: type,
			id: args.id,
		});

		const existingTrigger = cache.readFragment({
			id: ref?.__ref,
			fragment: gql`
			fragment My${type} on ${type} {
				${fields}
			}
		`,
		});

		return existingTrigger ? ref : undefined;
	};
